import React, { useState, useEffect } from 'react';
import { Building2, X, Loader2, Info } from 'lucide-react';
import {
  BillingAccount,
  BillingAccountFormData,
  emptyBillingAccountForm,
  getAvailableCountries,
  getSubdivisions,
  showTaxIdField,
  getStateLabel,
  getTaxIdLabel,
} from './billingTypes';
import SearchableSelect from './SearchableSelect';

interface BillingAccountFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BillingAccountFormData) => Promise<void>;
  editingAccount?: BillingAccount | null;
  serverRegion: string;
  loading?: boolean;
}

const BillingAccountForm: React.FC<BillingAccountFormProps> = ({
  isOpen,
  onClose,
  onSave,
  editingAccount,
  serverRegion,
  loading = false,
}) => {
  const [form, setForm] = useState<BillingAccountFormData>(emptyBillingAccountForm(serverRegion));
  const [saveHovered, setSaveHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);

  const availableCountries = getAvailableCountries(serverRegion);
  const subs = getSubdivisions(form.country);

  useEffect(() => {
    if (editingAccount) {
      setForm({
        name: editingAccount.name,
        first_name: editingAccount.first_name || '',
        last_name: editingAccount.last_name || '',
        billing_email: editingAccount.billing_email || '',
        company_name: editingAccount.company_name || '',
        address_line1: editingAccount.address_line1 || '',
        address_line2: editingAccount.address_line2 || '',
        city: editingAccount.city || '',
        state: editingAccount.state || '',
        postal_code: editingAccount.postal_code || '',
        country: editingAccount.country || '',
        vat_number: editingAccount.vat_number || '',
      });
    } else {
      setForm(emptyBillingAccountForm(serverRegion));
    }
  }, [editingAccount, serverRegion, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(form);
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid var(--v2-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--v2-text-tertiary)',
    margin: '4px 0 0 0',
  };

  const bodyStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const fieldGroupStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  };

  const buttonStyle = (
    primary: boolean,
    hovered: boolean,
    disabled: boolean
  ): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled
      ? 'var(--v2-border)'
      : primary
        ? hovered
          ? 'var(--v2-primary-hover)'
          : 'var(--v2-primary)'
        : hovered
          ? 'var(--v2-surface-hover)'
          : 'var(--v2-surface)',
    color: disabled ? 'var(--v2-text-muted)' : primary ? '#ffffff' : 'var(--v2-text)',
    border: primary ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  });

  const helperStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    color: 'var(--v2-text-tertiary)',
    marginTop: '6px',
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={titleStyle}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--v2-primary-light)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Building2 size={20} strokeWidth={1.5} color="var(--v2-primary)" />
              </div>
              {editingAccount ? 'Edit Billing Account' : 'New Billing Account'}
            </h2>
            <p style={subtitleStyle}>
              {editingAccount
                ? 'Update the billing account details'
                : 'Create a billing account to manage payments'}
            </p>
          </div>
          <button
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: closeHovered ? 'var(--v2-surface-hover)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--v2-text-muted)',
              transition: 'background-color 0.15s ease',
            }}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
        >
          <div style={bodyStyle}>
            {/* Tax guidance */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                backgroundColor: 'var(--v2-primary-light)',
                border: '1px solid var(--v2-border)',
                borderRadius: 'var(--radius-sm)',
                padding: '12px 14px',
                marginBottom: '16px',
                fontSize: '0.8125rem',
                color: 'var(--v2-text)',
              }}
            >
              <Info size={16} strokeWidth={1.5} color="var(--v2-primary)" style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>
                Make sure your billing <strong>country</strong> and address are accurate — they determine the tax
                on your invoices. If you're a business, add your <strong>VAT/Tax ID</strong> below to apply
                reverse charge (no tax added).
              </span>
            </div>

            {/* Account Name */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Account Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Personal, Company ABC"
                style={inputStyle}
                required
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
              <p style={helperStyle}>A friendly name to identify this billing account</p>
            </div>

            {/* Name Row */}
            <div style={rowStyle}>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>First Name</label>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Last Name</label>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>

            {/* Billing Email */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Billing Email</label>
              <input
                type="email"
                value={form.billing_email}
                onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
                placeholder="billing@company.com"
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* Company Name */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Company Name</label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* Country */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Country</label>
              <SearchableSelect
                value={form.country}
                onChange={(code) => setForm({ ...form, country: code, state: '' })}
                options={availableCountries}
                placeholder="Select country"
              />
            </div>

            {/* Address Line 1 */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Address Line 1</label>
              <input
                type="text"
                value={form.address_line1}
                onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* Address Line 2 */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Address Line 2</label>
              <input
                type="text"
                value={form.address_line2}
                onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* City */}
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                style={inputStyle}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>

            {/* State & Postal */}
            <div style={rowStyle}>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>{getStateLabel(form.country)}</label>
                {subs ? (
                  <SearchableSelect
                    value={form.state}
                    onChange={(code) => setForm({ ...form, state: code })}
                    options={subs}
                    placeholder="Select…"
                    openUp
                  />
                ) : (
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    style={inputStyle}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                )}
              </div>
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>Postal Code</label>
                <input
                  type="text"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
              </div>
            </div>

            {/* VAT / Tax ID */}
            {showTaxIdField(form.country) && (
              <div style={fieldGroupStyle}>
                <label style={labelStyle}>{getTaxIdLabel(form.country)}</label>
                <input
                  type="text"
                  value={form.vat_number}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                  placeholder="e.g. DE123456789"
                  style={inputStyle}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                />
                <p style={helperStyle}>
                  Business customers: enter your VAT/Tax ID to apply reverse charge (no tax added).
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              onMouseOver={() => setCancelHovered(true)}
              onMouseOut={() => setCancelHovered(false)}
              style={buttonStyle(false, cancelHovered, loading)}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.name.trim() || !form.country}
              onMouseOver={() => setSaveHovered(true)}
              onMouseOut={() => setSaveHovered(false)}
              style={buttonStyle(true, saveHovered, loading || !form.name.trim() || !form.country)}
            >
              {loading && (
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
              )}
              {loading ? 'Saving...' : editingAccount ? 'Update Account' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BillingAccountForm;
